import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormWatch,
} from 'react-hook-form';
import { useRef } from 'react';
import { Editor } from 'tinymce';
import {
  generalFieldMergeTags,
  triggerEventOptions,
} from '../trigger-event-options';
import MergeTagOnlyEditor from '../../../../shared/editor/mergetag-only-editor';
import ActionDescription from './action-description';
import { RulesEngineFormValues } from '../rules-engine-form-page';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface SendChatActionProps {
  description?: string;
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
}

export default function SendChatAction({
  description,
  control,
  errors,
  watch,
  clearErrors,
}: SendChatActionProps) {
  const editorRef = useRef<Editor | null>(null);

  const triggerEvent = watch('triggerInfo.triggerEvent');
  const currentTriggerEvent = triggerEventOptions.find(
    (option) => option.value === triggerEvent
  );

  const mergeTags = [
    generalFieldMergeTags,
    {
      title: 'Event Specific Fields',
      menu: currentTriggerEvent?.availableParameters.map((param) => {
        return {
          value: param.value,
          title: param.displayValue,
        };
      }),
    },
  ];

  return (
    <Stack>
      <ActionDescription description={description} />
      <Controller
        name="chatInfo.chatUrl"
        control={control}
        render={({ field }) => (
          <TextField
            label="Space Url"
            {...field}
            fullWidth
            onChange={(e) => {
              field.onChange(e);
              if (e.target.value) {
                clearErrors('chatInfo.chatUrl');
              }
            }}
            autoComplete="off"
            error={!!errors.chatInfo?.chatUrl}
            helperText={errors?.chatInfo?.chatUrl?.message || ' '}
          />
        )}
      />
      <Controller
        name="chatInfo.chatMessage"
        control={control}
        render={({ field }) => (
          <>
            <Box
              sx={
                errors.chatInfo?.chatMessage
                  ? {
                      height: 160,
                      border: (theme) =>
                        `1px solid ${theme.palette.error.main}`,
                      borderRadius: 3,
                    }
                  : {
                      height: 160,
                    }
              }
            >
              <MergeTagOnlyEditor
                {...field}
                editorRef={editorRef}
                content={field.value}
                setContent={() => {
                  field.onChange(editorRef.current?.getContent());
                  clearErrors('chatInfo.chatMessage');
                }}
                mergeTags={mergeTags}
                placeholder="Chat Message"
              />
            </Box>
            {errors.chatInfo?.chatMessage && (
              <Typography color="error" variant="caption" sx={{ ml: '14px' }}>
                {errors.chatInfo?.chatMessage.message}
              </Typography>
            )}
          </>
        )}
      />
    </Stack>
  );
}
